/**
 * KDBox — Wrapper around MUI Box
 *
 * Adds theme-aware styling shortcuts:
 * variant, bgColor, shadow, borderRadius, opacity, coloredShadow
 */

import { forwardRef } from "react";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

const KDBoxRoot = styled(Box)(({ theme, ownerState }) => {
  const { palette, functions, borders, boxShadows } = theme;
  const { variant, bgColor, color, opacity, borderRadius, shadow } = ownerState;

  const greyColors = {
    "grey-100": palette.grey[100],
    "grey-200": palette.grey[200],
    "grey-300": palette.grey[300],
    "grey-400": palette.grey[400],
    "grey-500": palette.grey[500],
    "grey-600": palette.grey[600],
    "grey-700": palette.grey[700],
    "grey-800": palette.grey[800],
    "grey-900": palette.grey[900],
  };

  // eslint-disable-next-line no-unused-vars
  const validColors = [
    "transparent", "white", "black", "primary", "secondary",
    "info", "success", "warning", "error", "light", "dark",
    "text", "grey-100", "grey-200", "grey-300", "grey-400",
    "grey-500", "grey-600", "grey-700", "grey-800", "grey-900",
  ];

  const getColor = (c) => {
    if (!c) return undefined;
    if (greyColors[c]) return greyColors[c];
    const split = c.split(".");
    const paletteColor = palette[split[0]];
    if (paletteColor) return split[1] ? paletteColor[split[1]] : paletteColor.main;
    return c;
  };

  // Background
  let backgroundValue = getColor(bgColor);
  if (variant === "gradient" && bgColor) {
    const gradientColor = palette.gradients?.[bgColor];
    if (gradientColor && functions?.linearGradient) {
      backgroundValue = functions.linearGradient(gradientColor.main, gradientColor.state);
    }
  }

  // Border radius
  let borderRadiusValue;
  if (borderRadius) {
    borderRadiusValue = borders?.borderRadius?.[borderRadius] || borderRadius;
  }

  // Box shadow
  let boxShadowValue;
  if (shadow) {
    boxShadowValue = boxShadows?.[shadow] || shadow;
  }

  return {
    opacity,
    background: backgroundValue,
    color: getColor(color),
    borderRadius: borderRadiusValue,
    boxShadow: boxShadowValue,
  };
});

const KDBox = forwardRef(
  ({ variant, bgColor, color, opacity, borderRadius, shadow, coloredShadow, ...rest }, ref) => (
    <KDBoxRoot
      {...rest}
      ref={ref}
      ownerState={{ variant, bgColor, color, opacity, borderRadius, shadow, coloredShadow }}
    />
  )
);

KDBox.displayName = "KDBox";

export default KDBox;
